import { useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Switch, Route, useLocation } from 'wouter';
import { SpeedInsights } from '@vercel/speed-insights/react';

import { resolveLegacyGeographyRedirect } from '../../config/legacy-geography-routes';

import { queryClient } from './lib/queryClient';
import NotFound from './pages/not-found';
import Home from './pages/home';
import Classes from './pages/classes';
import ClassDetail from './pages/class-detail';
import Creators from './pages/creators';
import CreatorProfile from './pages/creator-profile';
import Checkout from './pages/checkout';
import CheckoutConfirmation from './pages/checkout-confirmation';
import Community from './pages/community';
import BecomePartner from './pages/become-partner';
import About from './pages/about';
import Contact from './pages/contact';
import FAQ from './pages/faq';
import Login from './pages/login';
import VerifyEmail from './pages/verify-email';
import ResetPassword from './pages/reset-password';
import AdminDashboard from './pages/admin';
import Account from './pages/account';
import CreatorDashboard from './pages/creator-dashboard';
import VenueDashboard from './pages/venue-dashboard';
import AmbassadorDashboard from './pages/ambassador-dashboard';
import Onboarding from './pages/onboarding';
import StyleGuide from './pages/style-guide';
import RegionLanding from './pages/region-landing';
import GeographySelector from './pages/geography-selector';
import SearchPage from './pages/search';
import RegionClassesBrowse from './pages/region-classes-browse';
import RegionCreatorsBrowse from './pages/region-creators-browse';
import RegionCalendar from './pages/region-calendar';
import CalendarSubmit from './pages/calendar-submit';
import WidgetCalendar from './pages/widget-calendar';
import RegionCommunity from './pages/region-community';
import RegionLocationsBrowse from './pages/region-locations-browse';
import LocationDetail from './pages/location-detail';
import RegionUpdatesBrowse from './pages/region-updates-browse';
import UpdateDetail from './pages/update-detail';
import WaiverSign from './pages/waiver-sign';
import PassClaim from './pages/pass-claim';

import { ErrorBoundary } from '@/components/error-boundary';
import { ThemeProvider } from '@/components/theme-provider';
import { GeographyContextProvider } from '@/components/geography-context-provider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/hooks/useAuth';
import { POST_LOGIN_RETURN_KEY } from '@/lib/authUtils';

function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return null;
}

function LegacyCreatorApplyRedirect() {
  useEffect(() => {
    window.location.replace(`/creator/apply${window.location.search}`);
  }, []);

  return null;
}

// /class-dashboard is consolidated into /admin (Users & Classes section).
function LegacyClassDashboardRedirect() {
  useEffect(() => {
    window.location.replace('/admin?section=user-mgmt');
  }, []);

  return null;
}

function LegacyGeographyRedirect() {
  useEffect(() => {
    const redirectPath = resolveLegacyGeographyRedirect(window.location.pathname);
    window.location.replace(redirectPath ?? '/');
  }, []);

  return null;
}

function Router() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [location, navigate] = useLocation();

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) {
      return;
    }

    if (!user.hasCompletedOnboarding && location !== '/onboarding') {
      if (location && location !== '/') {
        window.sessionStorage.setItem(POST_LOGIN_RETURN_KEY, location);
      }
      navigate('/onboarding');
      return;
    }

    if (user.hasCompletedOnboarding && location === '/onboarding') {
      navigate('/');
      return;
    }

    // One-shot redirect: send creators to their dashboard on first landing after
    // login, but don't trap them there — they should be free to navigate anywhere.
    if (
      user.hasCompletedOnboarding &&
      user.role === 'creator' &&
      (location === '/' || location === '/login' || location === '/creator/apply')
    ) {
      const redirectDoneKey = `creator_redirect_done:${user.id}`;
      const alreadyRedirected = window.sessionStorage.getItem(redirectDoneKey);
      if (!alreadyRedirected) {
        const returnTo = window.sessionStorage.getItem(POST_LOGIN_RETURN_KEY);
        if (
          !returnTo ||
          returnTo === '/' ||
          returnTo === '/login' ||
          returnTo === '/creator/apply'
        ) {
          window.sessionStorage.removeItem(POST_LOGIN_RETURN_KEY);
          window.sessionStorage.setItem(redirectDoneKey, '1');
          navigate('/creator-dashboard');
          return;
        }
      }
    }

    // After Google OAuth, the server redirects to '/'. If a returnTo was stashed
    // before the OAuth redirect, send the user there now (one-shot: cleared immediately).
    if (user.hasCompletedOnboarding && location === '/') {
      const returnTo = window.sessionStorage.getItem(POST_LOGIN_RETURN_KEY);
      if (returnTo) {
        window.sessionStorage.removeItem(POST_LOGIN_RETURN_KEY);
        navigate(returnTo);
      }
    }
  }, [isAuthenticated, isLoading, location, navigate, user]);

  return (
    <ErrorBoundary resetKeys={[location]}>
      <Switch>
        {isLoading || !isAuthenticated ? (
          <>
            <Route path="/" component={GeographySelector} />
            <Route path="/login" component={Login} />
            <Route path="/verify-email" component={VerifyEmail} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route path="/about" component={About} />
            <Route path="/contact" component={Contact} />
            <Route path="/faq" component={FAQ} />
            <Route path="/creator/apply" component={BecomePartner} />
            <Route path="/classes" component={Classes} />
            <Route path="/classes/:id" component={ClassDetail} />
            <Route path="/creators" component={Creators} />
            <Route path="/creators/:id" component={CreatorProfile} />
            <Route path="/search" component={SearchPage} />
          </>
        ) : (
          <>
            <Route path="/" component={GeographySelector} />
            <Route path="/home" component={Home} />
            <Route path="/login" component={Login} />
            <Route path="/verify-email" component={VerifyEmail} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route path="/classes" component={Classes} />
            <Route path="/classes/:id" component={ClassDetail} />
            <Route path="/creators" component={Creators} />
            <Route path="/creators/:id" component={CreatorProfile} />
            <Route path="/search" component={SearchPage} />
            <Route path="/checkout" component={Checkout} />
            <Route path="/checkout/confirmation" component={CheckoutConfirmation} />
            <Route path="/community" component={Community} />
            <Route path="/creator/apply" component={BecomePartner} />
            <Route path="/about" component={About} />
            <Route path="/contact" component={Contact} />
            <Route path="/faq" component={FAQ} />
            <Route path="/admin" component={AdminDashboard} />
            <Route path="/class-dashboard" component={LegacyClassDashboardRedirect} />
            <Route path="/account" component={Account} />
            <Route path="/account/enrollments" component={Account} />
            <Route path="/onboarding" component={Onboarding} />
            <Route path="/creator-dashboard" component={CreatorDashboard} />
            <Route path="/venue-dashboard" component={VenueDashboard} />
            <Route path="/ambassador-dashboard" component={AmbassadorDashboard} />
          </>
        )}
        <Route path="/become-partner" component={LegacyCreatorApplyRedirect} />
        <Route path="/cities/:legacySlug/:section" component={LegacyGeographyRedirect} />
        <Route path="/cities/:legacySlug" component={LegacyGeographyRedirect} />
        {/* Geography pages — region and locality landing/browse */}
        <Route path="/regions/:regionSlug/classes" component={RegionClassesBrowse} />
        <Route path="/regions/:regionSlug/creators" component={RegionCreatorsBrowse} />
        <Route path="/regions/:regionSlug/calendar" component={RegionCalendar} />
        <Route path="/regions/:regionSlug/calendar/submit" component={CalendarSubmit} />
        <Route path="/regions/:regionSlug/community" component={RegionCommunity} />
        <Route path="/regions/:regionSlug/:localitySlug/classes" component={RegionClassesBrowse} />
        <Route
          path="/regions/:regionSlug/:localitySlug/creators"
          component={RegionCreatorsBrowse}
        />
        <Route path="/regions/:regionSlug/:localitySlug/calendar" component={RegionCalendar} />
        <Route
          path="/regions/:regionSlug/:localitySlug/calendar/submit"
          component={CalendarSubmit}
        />
        <Route path="/regions/:regionSlug/:localitySlug/community" component={RegionCommunity} />
        <Route
          path="/regions/:regionSlug/:localitySlug/locations/:locationSlug"
          component={LocationDetail}
        />
        <Route
          path="/regions/:regionSlug/:localitySlug/locations"
          component={RegionLocationsBrowse}
        />
        <Route path="/regions/:regionSlug/locations/:locationSlug" component={LocationDetail} />
        <Route path="/regions/:regionSlug/locations" component={RegionLocationsBrowse} />
        {/* Embeddable widget — no app chrome */}
        <Route path="/widget/calendar" component={WidgetCalendar} />
        {/* Blog/Updates routes */}
        <Route path="/regions/:regionSlug/:localitySlug/updates/:slug" component={UpdateDetail} />
        <Route path="/regions/:regionSlug/updates/:slug" component={UpdateDetail} />
        <Route path="/regions/:regionSlug/:localitySlug/updates" component={RegionUpdatesBrowse} />
        <Route path="/regions/:regionSlug/updates" component={RegionUpdatesBrowse} />
        <Route path="/waivers/sign/:token" component={WaiverSign} />
        <Route path="/passes/claim/:token" component={PassClaim} />
        <Route path="/regions/:regionSlug/:localitySlug" component={RegionLanding} />
        <Route path="/regions/:regionSlug" component={RegionLanding} />
        {/* Dev-only style guide — not in production nav */}
        <Route path="/style-guide" component={StyleGuide} />
        <Route component={NotFound} />
      </Switch>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <GeographyContextProvider>
          <TooltipProvider>
            <Toaster />
            <ScrollToTop />
            <Router />
            <SpeedInsights />
          </TooltipProvider>
        </GeographyContextProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
